// RFC-0944 shared rules editor (RulesEditor).
//
// Props: `{scope, initialDocument, initialVersion, onSave, isReadOnly,
// history, autoFocusBody, onDirtyChange}`. Renders a <textarea> for the body,
// a schema-driven form for the structured fields, an empty-state view with
// click-to-insert examples, and a collapsed history/revert disclosure. The
// Save button emits op="replace_body" + `expected_version` from last-loaded
// state; 409 triggers a conflict message. Revert buttons emit op="revert" +
// `revert_to_version`.
//
// Types come from `apps/console/src/generated/rulesDocument.ts` — no local
// intersections per CLAUDE.md → Schema-Driven Invariants #6. Styling uses the
// Tailwind design tokens (surface/border/foreground) so it drops into the
// UserRulesModal chrome; there is no bespoke `rules-editor__*` CSS.

import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, ChevronDown, ChevronUp, X } from 'lucide-react'

import {
  CONTENT_LANGUAGE_CODES,
  RULES_MAX_BODY_BYTES,
  RULES_MAX_STRUCTURED_LIST_LENGTH,
  RULES_MAX_STRUCTURED_LIST_ITEM_BYTES,
  type RulesDocumentV1,
  type RulesConflictDetail,
  type RulesHistoryEntry,
  type RulesPutBody,
  utf8ByteLength,
  validateRulesDocument,
} from '../../generated/rulesDocument'
import {
  emptyRulesDocument,
  parseRulesConflictResponse,
  RulesRequestError,
} from '../../api/rules'
import { createNadDebug } from '../../utils/debugLog'
import { resultOf, trackInteraction } from '../../analytics/interactionAnalytics'
import { useOptionalProjectId } from '../../contexts/ProjectIdContext'

const SALIENCE_CAP = 6
const conflictLog = createNadDebug('dw:rules-conflict-recovery')

export type RulesScope = 'user' | 'project'

export interface RulesEditorProps {
  scope: RulesScope
  initialDocument: RulesDocumentV1 | null
  initialVersion: number
  onSave(body: RulesPutBody): Promise<{ new_version: number; document: RulesDocumentV1 } | void>
  isReadOnly?: boolean
  history?: RulesHistoryEntry[]
  /** Focus the body textarea on mount (used when hosted in a modal). */
  autoFocusBody?: boolean
  /** Reports whether the working document differs from the loaded baseline. */
  onDirtyChange?: (dirty: boolean) => void
}

// i18n keys under `pages.rules.emptyState.*` — sourced from the shared pages
// namespace so target-locale translations track source. Click-to-insert examples resolve
// against the same keys the empty-state view renders.
const EMPTY_STATE_KEYS: ReadonlyArray<'example1' | 'example2' | 'example3'> = [
  'example1',
  'example2',
  'example3',
]

const LIST_FIELDS: ReadonlyArray<'banned_terms' | 'always_include' | 'tone'> = [
  'banned_terms',
  'always_include',
  'tone',
]

function _isEmptyDoc(doc: RulesDocumentV1 | null): boolean {
  if (doc === null) return true
  if (doc.body.trim().length > 0) return false
  const s = doc.structured
  if (s.tone.length > 0) return false
  if (s.banned_terms.length > 0) return false
  if (s.always_include.length > 0) return false
  if (s.content_language_hint !== null) return false
  if (s.aspect_ratio_default !== null) return false
  return true
}

const INPUT_CLASS =
  'w-full rounded-lg border border-input-border bg-input-bg px-3 py-2 text-sm text-foreground ' +
  'placeholder:text-text-muted focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 ' +
  'disabled:opacity-50'

const PRIMARY_BUTTON_CLASS =
  'rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground ' +
  'transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50'

const SECONDARY_BUTTON_CLASS =
  'rounded-lg border border-input-border bg-surface-1 px-2.5 py-1 text-xs font-medium text-foreground ' +
  'transition-colors hover:border-input-border-hover hover:bg-surface-3 focus-visible:outline-none ' +
  'focus-visible:ring-2 focus-visible:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-50'

interface ActiveRulesConflict {
  detail: RulesConflictDetail
  operation: 'replace_body' | 'revert'
  revertToVersion: number | null
}

function _structuredHintCount(doc: RulesDocumentV1): number {
  const structured = doc.structured
  return (
    structured.tone.length +
    structured.banned_terms.length +
    structured.always_include.length +
    (structured.content_language_hint === null ? 0 : 1) +
    (structured.aspect_ratio_default === null ? 0 : 1)
  )
}

export default function RulesEditor(props: RulesEditorProps): JSX.Element {
  const {
    scope,
    initialDocument,
    initialVersion,
    onSave,
    isReadOnly = false,
    history = [],
    autoFocusBody = false,
    onDirtyChange,
  } = props
  const { t } = useTranslation('pages')
  const [doc, setDoc] = useState<RulesDocumentV1>(
    () => initialDocument ?? emptyRulesDocument(),
  )
  const [expectedVersion, setExpectedVersion] = useState<number>(initialVersion)
  const [savingState, setSavingState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [activeConflict, setActiveConflict] = useState<ActiveRulesConflict | null>(null)
  const [showHistory, setShowHistory] = useState(false)
  const bodyRef = useRef<HTMLTextAreaElement>(null)
  const useLatestRef = useRef<HTMLButtonElement>(null)

  // JSON snapshot of the loaded baseline — used to derive the dirty flag.
  const baselineRef = useRef<string>(
    JSON.stringify(initialDocument ?? emptyRulesDocument()),
  )

  useEffect(() => {
    const next = initialDocument ?? emptyRulesDocument()
    setDoc(next)
    setExpectedVersion(initialVersion)
    baselineRef.current = JSON.stringify(next)
    setActiveConflict(null)
  }, [initialDocument, initialVersion])

  // Focus the body textarea when hosted in a modal (Modal.tsx does not set
  // initial focus). Runs once on mount — the editor is remounted each time the
  // modal reopens because the modal unmounts its children while closed.
  useEffect(() => {
    if (autoFocusBody) bodyRef.current?.focus()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Derive emptiness from the LIVE doc (not initialDocument) so inserting a
  // click-to-insert example immediately dismisses the empty-state panel.
  const isEmpty = useMemo(() => _isEmptyDoc(doc), [doc])
  const bodyByteCount = useMemo(() => utf8ByteLength(doc.body), [doc.body])
  const validationError = useMemo(() => validateRulesDocument(doc), [doc])
  const dirty = useMemo(
    () => JSON.stringify(doc) !== baselineRef.current,
    [doc],
  )
  const showConflictComparison =
    activeConflict !== null &&
    (activeConflict.operation === 'replace_body' || dirty)

  useEffect(() => {
    onDirtyChange?.(dirty)
  }, [dirty, onDirtyChange])

  useEffect(() => {
    if (!showConflictComparison || activeConflict === null) return
    conflictLog({
      event: 'rules-conflict:comparison_presented',
      trace_id: `rules-conflict:${scope}`,
      scope,
      current_version: activeConflict.detail.current_version,
      latest_snapshot_present: true,
      draft_preserved: true,
    })
  }, [activeConflict, scope, showConflictComparison])

  useEffect(() => {
    if (activeConflict !== null) useLatestRef.current?.focus()
  }, [activeConflict])

  const analyticsProjectId = useOptionalProjectId()
  // The persisted outcome of a save is the server's rules_save_result; this is the click.
  const trackRulesAction = (action: 'save' | 'revert' | 'insert_example', ok?: boolean) => {
    trackInteraction('settings_rules_action', {
      action,
      scope,
      project_id: scope === 'project' ? analyticsProjectId : undefined,
      result: ok === undefined ? undefined : resultOf(ok),
    })
  }

  const handleInsertExample = (example: string) => {
    trackRulesAction('insert_example')
    setDoc((prev) => ({
      ...prev,
      body: prev.body ? `${prev.body}\n${example}` : example,
    }))
  }

  const applySuccessfulWrite = (
    result: { new_version: number; document: RulesDocumentV1 } | void,
  ) => {
    if (result) {
      setExpectedVersion(result.new_version)
      setDoc(result.document)
      baselineRef.current = JSON.stringify(result.document)
    }
    setActiveConflict(null)
    setSavingState('saved')
  }

  const writeRules = async (body: RulesPutBody) => {
    setSavingState('saving')
    setErrorMessage(null)
    setActiveConflict(null)
    try {
      const result = await onSave(body)
      applySuccessfulWrite(result)
      return result?.new_version ?? null
    } catch (err) {
      if (err instanceof RulesRequestError && err.status === 409) {
        try {
          const parsed = parseRulesConflictResponse(err.body)
          if (parsed !== null) {
            setActiveConflict({
              detail: parsed.detail,
              operation: body.op,
              revertToVersion: body.op === 'revert' ? body.revert_to_version : null,
            })
            setErrorMessage(null)
            setSavingState('error')
            conflictLog({
              event: 'rules-conflict:detected',
              trace_id: `rules-conflict:${scope}`,
              scope,
              operation: body.op,
              reason: parsed.detail.reason,
              expected_version: parsed.detail.expected_version,
              current_version: parsed.detail.current_version,
            })
            return null
          }
        } catch (parseError) {
          const message =
            parseError instanceof Error ? parseError.message : String(parseError)
          setActiveConflict(null)
          setErrorMessage(t('rules.saveFailedPrefix', { message }))
          setSavingState('error')
          return null
        }
        // Rolling-deploy compatibility for an older API 409 without the atomic
        // snapshot. Do not guess at current state from an incomplete payload.
        setActiveConflict(null)
        setErrorMessage(t('rules.conflict'))
        setSavingState('error')
        return null
      }

      const message = err instanceof Error ? err.message : String(err)
      setActiveConflict(null)
      setErrorMessage(
        t(body.op === 'revert' ? 'rules.revertFailedPrefix' : 'rules.saveFailedPrefix', {
          message,
        }),
      )
      setSavingState('error')
      return null
    }
  }

  const handleSave = async () => {
    if (isReadOnly) return
    if (validationError !== null) {
      setErrorMessage(validationError)
      setSavingState('error')
      return
    }
    const saved = await writeRules({
      op: 'replace_body',
      document: doc,
      expected_version: expectedVersion,
    })
    trackRulesAction('save', saved !== null)
  }

  const handleRevert = async (version: number) => {
    if (isReadOnly) return
    const reverted = await writeRules({
      op: 'revert',
      revert_to_version: version,
      expected_version: expectedVersion,
    })
    trackRulesAction('revert', reverted !== null)
  }

  const handleUseLatest = () => {
    if (activeConflict === null) return
    const latest = activeConflict.detail
    setDoc(latest.current_document)
    setExpectedVersion(latest.current_version)
    baselineRef.current = JSON.stringify(latest.current_document)
    setActiveConflict(null)
    setErrorMessage(null)
    setSavingState('idle')
    conflictLog({
      event: 'rules-conflict:latest_adopted',
      trace_id: `rules-conflict:${scope}`,
      scope,
      current_version: latest.current_version,
    })
  }

  const handleReapply = async () => {
    if (activeConflict === null) return
    if (validationError !== null) {
      setErrorMessage(validationError)
      setSavingState('error')
      return
    }
    const { detail, operation, revertToVersion } = activeConflict
    if (detail.reason === 'revert_target_missing') return
    conflictLog({
      event: 'rules-conflict:reapply_selected',
      trace_id: `rules-conflict:${scope}`,
      scope,
      operation,
      current_version: detail.current_version,
    })
    if (operation === 'revert') {
      if (revertToVersion === null) {
        setErrorMessage(t('rules.conflictMissingRevertTarget'))
        setSavingState('error')
        return
      }
      const resolvedVersion = await writeRules({
        op: 'revert',
        revert_to_version: revertToVersion,
        expected_version: detail.current_version,
      })
      if (resolvedVersion !== null) {
        conflictLog({
          event: 'rules-conflict:resolved',
          trace_id: `rules-conflict:${scope}`,
          scope,
          operation,
          based_on_version: detail.current_version,
          persisted_version: resolvedVersion,
        })
      }
      return
    }
    const resolvedVersion = await writeRules({
      op: 'replace_body',
      document: doc,
      expected_version: detail.current_version,
    })
    if (resolvedVersion !== null) {
      conflictLog({
        event: 'rules-conflict:resolved',
        trace_id: `rules-conflict:${scope}`,
        scope,
        operation,
        based_on_version: detail.current_version,
        persisted_version: resolvedVersion,
      })
    }
  }

  const headingCopy =
    scope === 'project'
      ? t('rules.headingProjectScope')
      : t('rules.headingUserScope')

  return (
    <div
      className="flex flex-col gap-5 text-foreground"
      data-scope={scope}
      data-testid="rules-editor"
    >
      <header className="flex flex-col gap-1">
        <h2 className="text-sm font-semibold text-foreground">{headingCopy}</h2>
        <p className="text-sm text-muted-foreground">{t('rules.pitch')}</p>
      </header>

      {isEmpty ? (
        <section
          className="flex flex-col gap-2 rounded-md border border-border-subtle bg-surface-2 p-4"
          data-testid="rules-editor-empty"
        >
          <p className="text-sm text-muted-foreground">{t('rules.emptyState.intro')}</p>
          <ul className="flex flex-col gap-1.5">
            {EMPTY_STATE_KEYS.map((key) => {
              const example = t(`rules.emptyState.${key}`)
              return (
                <li key={key}>
                  <button
                    type="button"
                    onClick={() => handleInsertExample(example)}
                    disabled={isReadOnly}
                    data-testid={`rules-editor-example-${key}`}
                    className="w-full rounded-md border border-border bg-surface-1 px-3 py-1.5 text-left text-sm text-foreground transition-colors hover:bg-surface-3 disabled:opacity-50"
                  >
                    {example}
                  </button>
                </li>
              )
            })}
          </ul>
          <p className="text-xs font-medium text-muted-foreground">{t('rules.emptyState.director')}</p>
        </section>
      ) : null}

      <section className="flex flex-col gap-1.5">
        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-muted-foreground">
            {t('rules.body.label', {
              bytes: bodyByteCount,
              limit: RULES_MAX_BODY_BYTES,
            })}
          </span>
          <textarea
            ref={bodyRef}
            value={doc.body}
            onChange={(e) => setDoc((prev) => ({ ...prev, body: e.target.value }))}
            rows={8}
            disabled={isReadOnly}
            data-testid="rules-editor-body"
            className={`${INPUT_CLASS} resize-y font-mono`}
          />
        </label>
      </section>

      <section className="flex flex-col gap-3">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {t('rules.structuredHeading')}
        </h3>
        {LIST_FIELDS.map((field) => (
          <StructuredList
            key={field}
            scope={scope}
            fieldName={field}
            items={doc.structured[field]}
            disabled={isReadOnly}
            onChange={(next) =>
              setDoc((prev) => ({
                ...prev,
                structured: { ...prev.structured, [field]: next },
              }))
            }
          />
        ))}
        <StructuredLanguageSelect
          scope={scope}
          value={doc.structured.content_language_hint}
          disabled={isReadOnly}
          onChange={(next) =>
            setDoc((prev) => ({
              ...prev,
              structured: { ...prev.structured, content_language_hint: next },
            }))
          }
        />
        {/* aspect_ratio_default is intentionally NOT surfaced here: aspect ratio
            is a project-level config (ProjectConfigGear), so exposing it as a
            rules hint would confuse users. The schema field is retained for
            back-compat; it just has no editor UI. */}
      </section>

      {history.length > 0 ? (
        <section className="rounded-md border border-border-subtle">
          <button
            type="button"
            onClick={() => setShowHistory((v) => !v)}
            aria-expanded={showHistory}
            data-testid="rules-editor-history-toggle"
            className="flex w-full items-center justify-between px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-surface-2"
          >
            <span>{t('rules.historyToggle', { count: history.length })}</span>
            {showHistory ? (
              <ChevronUp className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            )}
          </button>
          {showHistory ? (
            <ol
              className="flex flex-col gap-1 border-t border-border-subtle px-3 py-2"
              data-testid="rules-editor-history"
            >
              {history.map((entry) => (
                <li
                  key={entry.version}
                  className="flex items-center justify-between gap-2 text-xs font-medium text-muted-foreground"
                >
                  <span className="truncate">{`v${entry.version} · ${entry.updated_by} · ${entry.updated_at}`}</span>
                  <button
                    type="button"
                    disabled={isReadOnly || savingState === 'saving'}
                    onClick={() => handleRevert(entry.version)}
                    className={SECONDARY_BUTTON_CLASS}
                  >
                    {t('rules.revert')}
                  </button>
                </li>
              ))}
            </ol>
          ) : null}
        </section>
      ) : null}

      <footer className="flex flex-col gap-2">
        {activeConflict ? (
          <section
            role="group"
            aria-labelledby="rules-editor-conflict-title"
            data-testid="rules-editor-conflict"
            className="flex flex-col gap-3 rounded-xl border border-warning/40 bg-warning/10 p-4"
          >
            <div className="flex gap-3" role="alert">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-warning/15 text-warning">
                <AlertTriangle className="h-4 w-4" aria-hidden="true" />
              </div>
              <div className="min-w-0">
                <h3
                  id="rules-editor-conflict-title"
                  className="text-sm font-semibold text-foreground"
                >
                  {t(
                    activeConflict.detail.reason === 'revert_target_missing'
                      ? 'rules.conflictTargetMissingTitle'
                      : 'rules.conflictTitle',
                  )}
                </h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  {t(
                    activeConflict.detail.reason === 'revert_target_missing'
                      ? 'rules.conflictTargetMissingDescription'
                      : 'rules.conflictDescription',
                  )}
                </p>
              </div>
            </div>

            {showConflictComparison ? (
              <div className="grid gap-2 sm:grid-cols-2">
                {[
                  {
                    key: 'latest',
                    label: t('rules.conflictLatest', {
                      version: activeConflict.detail.current_version,
                    }),
                    document: activeConflict.detail.current_document,
                  },
                  {
                    key: 'draft',
                    label: t('rules.conflictDraft'),
                    document: doc,
                  },
                ].map((item) => (
                  <div
                    key={item.key}
                    data-testid={`rules-editor-conflict-${item.key}`}
                    className="min-w-0 rounded-lg border border-border bg-surface-1 p-3"
                  >
                    <p className="text-xs font-medium text-muted-foreground">{item.label}</p>
                    <p className="mt-2 line-clamp-3 whitespace-pre-wrap text-sm text-foreground">
                      {item.document.body.trim() || t('rules.conflictNoBody')}
                    </p>
                    <dl className="mt-2 space-y-1 text-xs font-medium">
                      {LIST_FIELDS.map((field) =>
                        item.document.structured[field].length > 0 ? (
                          <div key={field} className="grid grid-cols-[auto_1fr] gap-2">
                            <dt className="text-text-muted">{t(`rules.field.${field}`)}</dt>
                            <dd className="min-w-0 break-words text-muted-foreground">
                              {item.document.structured[field].join(' · ')}
                            </dd>
                          </div>
                        ) : null,
                      )}
                      {item.document.structured.content_language_hint !== null ? (
                        <div className="grid grid-cols-[auto_1fr] gap-2">
                          <dt className="text-text-muted">
                            {t('rules.field.content_language_hint')}
                          </dt>
                          <dd className="text-muted-foreground">
                            {item.document.structured.content_language_hint}
                          </dd>
                        </div>
                      ) : null}
                    </dl>
                    <p className="mt-2 text-xs font-medium text-text-muted">
                      {t('rules.conflictHintCount', {
                        count: _structuredHintCount(item.document),
                      })}
                    </p>
                  </div>
                ))}
              </div>
            ) : null}

            <div className="flex flex-wrap justify-end gap-2">
              <button
                type="button"
                ref={useLatestRef}
                onClick={handleUseLatest}
                data-testid="rules-editor-conflict-use-latest"
                className="h-9 rounded-lg border border-input-border bg-surface-1 px-3 text-sm font-medium text-foreground transition-colors hover:border-input-border-hover hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
              >
                {t('rules.conflictUseLatest')}
              </button>
              {activeConflict.detail.reason !== 'revert_target_missing' ? (
                <button
                  type="button"
                  onClick={handleReapply}
                  disabled={savingState === 'saving' || validationError !== null}
                  data-testid="rules-editor-conflict-reapply"
                  className="h-9 rounded-lg bg-primary px-3 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {t(
                    activeConflict.operation === 'revert'
                      ? 'rules.conflictRetryRevert'
                      : 'rules.conflictReapply',
                  )}
                </button>
              ) : null}
            </div>
          </section>
        ) : null}
        {errorMessage ? (
          <p className="text-sm text-red-400" data-testid="rules-editor-error">
            {errorMessage}
          </p>
        ) : null}
        {validationError ? (
          <p className="text-xs font-medium text-red-400" data-testid="rules-editor-validation">
            {t('rules.validationPrefix', { reason: validationError })}
          </p>
        ) : null}
        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleSave}
            disabled={
              isReadOnly ||
              savingState === 'saving' ||
              validationError !== null ||
              activeConflict !== null
            }
            data-testid="rules-editor-save"
            className={PRIMARY_BUTTON_CLASS}
          >
            {savingState === 'saving' ? t('rules.saving') : t('rules.save')}
          </button>
        </div>
      </footer>
    </div>
  )
}

interface StructuredListProps {
  scope: RulesScope
  fieldName: 'banned_terms' | 'always_include' | 'tone'
  items: string[]
  onChange(next: string[]): void
  disabled: boolean
}

function StructuredList(props: StructuredListProps): JSX.Element {
  const { scope, fieldName, items, onChange, disabled } = props
  const { t } = useTranslation('pages')
  const [draft, setDraft] = useState('')

  const handleAdd = () => {
    if (!draft.trim()) return
    if (items.length >= RULES_MAX_STRUCTURED_LIST_LENGTH) return
    if (utf8ByteLength(draft) > RULES_MAX_STRUCTURED_LIST_ITEM_BYTES) return
    onChange([...items, draft.trim()])
    setDraft('')
  }

  const handleRemove = (idx: number) => {
    const next = items.slice()
    next.splice(idx, 1)
    onChange(next)
  }

  return (
    <div className="flex flex-col gap-1.5" data-field={fieldName}>
      <span className="text-xs font-medium text-muted-foreground">
        {t(`rules.field.${fieldName}`)}
      </span>
      <span className="text-xs font-medium text-muted-foreground/70">
        {t(`rules.desc.${fieldName}`)}
      </span>
      {items.length > 0 ? (
        <ul className="flex flex-wrap gap-1.5">
          {items.map((item, idx) => (
            <li
              key={`${item}-${idx}`}
              className={`flex items-center gap-1.5 rounded-md border border-border px-2 py-1 text-xs font-medium ${
                idx >= SALIENCE_CAP ? 'opacity-60' : ''
              }`}
            >
              <span className="text-foreground">{item}</span>
              {!disabled ? (
                <button
                  type="button"
                  onClick={() => handleRemove(idx)}
                  aria-label={t('rules.remove')}
                  className="text-muted-foreground transition-colors hover:text-foreground"
                >
                  <X className="h-3 w-3" />
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      <div className="flex gap-2">
        <input
          type="text"
          value={draft}
          aria-label={t('rules.addItemLabel', { field: t(`rules.field.${fieldName}`) })}
          placeholder={t('rules.addItemPlaceholder')}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              handleAdd()
            }
          }}
          disabled={disabled}
          data-testid={`rules-editor-${scope}-${fieldName}-add`}
          className={INPUT_CLASS}
        />
        <button
          type="button"
          onClick={handleAdd}
          disabled={disabled}
          className={SECONDARY_BUTTON_CLASS}
        >
          {t('rules.add')}
        </button>
      </div>
    </div>
  )
}

interface StructuredLanguageSelectProps {
  scope: RulesScope
  value: string | null
  onChange(next: string | null): void
  disabled: boolean
}

// Content-language hint editor. Users don't know ISO 639-1 codes (en/vi/…), so
// this is a <select> of the exact codes the backend validates against
// (CONTENT_LANGUAGE_CODES, codegen'd from content_language.py). Labels are
// resolved via Intl.DisplayNames so they localize with the UI, falling back to
// the bare code (e.g. `fil`, which has no ISO 639-1 name). Empty selection
// clears the field back to null.
function StructuredLanguageSelect(props: StructuredLanguageSelectProps): JSX.Element {
  const { scope, value, onChange, disabled } = props
  const { t, i18n } = useTranslation('pages')
  const options = useMemo(() => {
    let display: Intl.DisplayNames | null = null
    try {
      display = new Intl.DisplayNames([i18n.language], { type: 'language' })
    } catch {
      display = null
    }
    return CONTENT_LANGUAGE_CODES.map((code) => {
      const name = display ? display.of(code) : undefined
      return { code, label: name && name !== code ? `${name} (${code})` : code }
    })
  }, [i18n.language])
  return (
    <div className="flex flex-col gap-1.5" data-field="content_language_hint">
      <label className="flex flex-col gap-1.5">
        <span className="text-xs font-medium text-muted-foreground">
          {t('rules.field.content_language_hint')}
        </span>
        <span className="text-xs font-medium text-muted-foreground/70">
          {t('rules.desc.content_language_hint')}
        </span>
        <select
          value={value ?? ''}
          onChange={(e) => {
            const next = e.target.value
            onChange(next === '' ? null : next)
          }}
          disabled={disabled}
          data-testid={`rules-editor-${scope}-content_language_hint`}
          className={INPUT_CLASS}
        >
          <option value="">{t('rules.languageNone')}</option>
          {options.map((o) => (
            <option key={o.code} value={o.code}>
              {o.label}
            </option>
          ))}
        </select>
      </label>
    </div>
  )
}
